import { Fragment, useMemo } from "react";
import { CornerDownRight } from "lucide-react";

import { statusTokenClasses } from "@/components/status-badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  compareScheduleRows,
  formatScheduleDate,
  formatSchedulePercent,
  formatVarianceDays,
  parseDateKeyToMs,
  scheduleStatusDot,
  type FieldPilotScheduleRow,
} from "@/features/field-pilot/schedule-format";
import {
  FIELD_PILOT_STATUS_FALLBACK_COLOR,
  StatusDot,
} from "@/features/field-pilot/status-dot";
import type {
  FieldPilotSubTaskMilestone,
  FieldPilotSubTaskRow,
} from "@/features/field-pilot/sub-task-rows";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A schedule row that may also carry its rolled-up sub-tasks. Sub-tasks are optional so the
 * visualizations accept both plain rows (e.g. tests, the read-only main table) and the richer
 * rows returned by `getScheduleByJob`.
 */
export type ScheduleRowInput = FieldPilotScheduleRow & {
  hasSubTasks?: boolean;
  subTasks?: FieldPilotSubTaskRow[];
};

function isSoloItemSchedule(subTask: Pick<FieldPilotSubTaskRow, "itemCount">): boolean {
  return subTask.itemCount === 1;
}

/** Sort sub-tasks earliest-start first so nested Gantt rows cascade in waterfall order. */
function compareSubTasksByStartDate(a: FieldPilotSubTaskRow, b: FieldPilotSubTaskRow): number {
  return compareScheduleRows(
    { startDateKey: a.startDateKey, displayName: a.name },
    { startDateKey: b.startDateKey, displayName: b.name }
  );
}

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function progressColor(row: { hasSchedule: boolean; isBehind: boolean }) {
  if (!row.hasSchedule) return "#9CA3AF";
  return row.isBehind ? "#EF4444" : "#22C55E";
}

function formatDateRange(
  startDateKey: string | null,
  finishDateKey: string | null
): string | null {
  if (!startDateKey && !finishDateKey) return null;
  return `${formatScheduleDate(startDateKey)} – ${formatScheduleDate(finishDateKey)}`;
}

/**
 * Compact actual-vs-expected progress bar. The fill shows actual progress toward final
 * completion; a tick marks the amount expected by today.
 */
export function ScheduleProgressBar({
  row,
  showExpectedMarker = true,
  className,
}: {
  row: Pick<
    FieldPilotScheduleRow,
    "actualFraction" | "expectedFraction" | "hasSchedule" | "isBehind"
  >;
  showExpectedMarker?: boolean;
  className?: string;
}) {
  const actualPct = Math.round(clampFraction(row.actualFraction) * 100);
  const expectedPct = Math.round(clampFraction(row.expectedFraction) * 100);
  return (
    <div
      className={cn("relative h-2.5 w-full overflow-hidden rounded-full bg-muted", className)}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${actualPct}%`, backgroundColor: progressColor(row) }}
      />
      {showExpectedMarker && row.hasSchedule ? (
        <span
          className="absolute top-1/2 h-3.5 w-0.5 -translate-y-1/2 rounded-full bg-foreground/70"
          style={{ left: `calc(${expectedPct}% - 1px)` }}
          title={`Expected by today ${expectedPct}%`}
        />
      ) : null}
    </div>
  );
}

/**
 * A thin stacked proportion bar — one segment per status, width proportional to that status's
 * share — with a tooltip listing every status, its color, and its percentage. `percentage` is a
 * 0..1 fraction. Zero-width statuses are omitted from the bar but still listed in the tooltip.
 */
export interface StatusBreakdownEntry {
  statusId: string;
  name: string;
  colorHex?: string | null;
  percentage: number;
}

export function StatusBreakdownBar({
  breakdown,
}: {
  breakdown: StatusBreakdownEntry[];
}) {
  const entries = breakdown ?? [];
  if (entries.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const summary = entries
    .map((entry) => `${entry.name} ${formatSchedulePercent(entry.percentage)}`)
    .join(" · ");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex h-2.5 w-full min-w-[120px] max-w-[240px] overflow-hidden rounded-full border bg-muted"
          role="img"
          aria-label={summary}
        >
          {entries.map((entry) => {
            const pct = clampFraction(entry.percentage) * 100;
            if (pct <= 0) return null;
            return (
              <span
                key={entry.statusId}
                className="h-full"
                style={{
                  width: `${pct}%`,
                  backgroundColor: entry.colorHex ?? FIELD_PILOT_STATUS_FALLBACK_COLOR,
                }}
              />
            );
          })}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="flex flex-col gap-1">
          {entries.map((entry) => (
            <span key={entry.statusId} className="flex items-center gap-2 text-xs">
              <StatusDot colorHex={entry.colorHex} />
              <span className="me-3 truncate">{entry.name}</span>
              <span className="ms-auto tabular-nums">
                {formatSchedulePercent(entry.percentage)}
              </span>
            </span>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function ScheduleRowLabel({ row }: { row: ScheduleRowInput }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-sm font-medium">{row.displayName}</div>
      <div className="truncate text-[11px] text-muted-foreground">
        {row.isCustom ? "Custom" : row.rawValue}
      </div>
    </div>
  );
}

/** Right-aligned actual / expected / on-track metrics shared by the progress view rows. */
function ProgressMetrics({
  row,
}: {
  row: Pick<FieldPilotScheduleRow, "actualFraction" | "expectedFraction" | "hasSchedule" | "isBehind">;
}) {
  const dot = scheduleStatusDot(row);
  return (
    <div className="flex items-center gap-3 whitespace-nowrap text-xs">
      <span className="font-semibold">Actual {formatSchedulePercent(row.actualFraction)}</span>
      <span className="text-muted-foreground">
        exp {row.hasSchedule ? formatSchedulePercent(row.expectedFraction) : "—"}
      </span>
      <span
        className={statusTokenClasses(dot.status, {
          visual: "dot",
          size: "lg",
          tone: dot.tone,
        })}
        role="img"
        title={dot.label}
        aria-label={dot.label}
      />
    </div>
  );
}

const PROGRESS_GRID = "grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] items-center gap-4";

/** A progress row for a sub-task or single-item schedule. */
function SubTaskProgressRow({ subTask }: { subTask: FieldPilotSubTaskRow }) {
  const dateRange = formatDateRange(subTask.startDateKey, subTask.finishDateKey);
  const datedMilestones = (subTask.milestones ?? []).filter((m) => m.dueDateKey);
  const isSolo = isSoloItemSchedule(subTask);
  return (
    <div className={PROGRESS_GRID}>
      <div className="flex min-w-0 items-center gap-1.5">
        {!isSolo ? (
          <CornerDownRight
            className="size-3.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        ) : null}
        <div className="min-w-0">
          <div
            className={cn("truncate font-medium", isSolo ? "text-sm" : "text-xs")}
            title={subTask.name}
          >
            {subTask.name}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {subTask.currentStatus?.name ?? "—"}
            {dateRange ? ` · ${dateRange}` : ""}
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <ScheduleProgressBar row={subTask} />
        {datedMilestones.length > 0 ? (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {datedMilestones.map((milestone) => (
              <span
                key={String(milestone.statusId)}
                className={cn(
                  "inline-flex items-center gap-1 text-[10px]",
                  milestone.reached ? "font-medium text-foreground" : "text-muted-foreground"
                )}
              >
                <StatusDot colorHex={milestone.colorHex} className="size-1.5" />
                <span className="truncate">{milestone.name ?? "Status"}</span>
                <span className="tabular-nums">{formatScheduleDate(milestone.dueDateKey)}</span>
                <span>{milestone.reached ? "✓ reached" : `${formatSchedulePercent(milestone.fraction)} reached`}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <ProgressMetrics row={subTask} />
    </div>
  );
}

/** Per-row expected-vs-actual progress bars, sorted by start date, with nested sub-tasks. */
export function ScheduleProgressView({ rows }: { rows: ScheduleRowInput[] }) {
  const sorted = useMemo(() => rows.slice().sort(compareScheduleRows), [rows]);
  if (sorted.length === 0) {
    return <EmptyState />;
  }
  return (
    <div className="space-y-3">
      {sorted.map((row) => {
        const subTasks = row.subTasks ?? [];
        return (
          <div key={row.tagId} className="rounded-lg border">
            <div className={cn(PROGRESS_GRID, "px-3 py-2")}>
              <ScheduleRowLabel row={row} />
              <ScheduleProgressBar row={row} />
              <ProgressMetrics row={row} />
            </div>
            {subTasks.length > 0 ? (
              <div className="space-y-2 border-t bg-muted/20 px-3 py-2">
                {subTasks.map((subTask) => (
                  <SubTaskProgressRow key={String(subTask.subTaskId)} subTask={subTask} />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

interface GanttBounds {
  minMs: number;
  maxMs: number;
  spanMs: number;
}

/**
 * Compute the shared Gantt time-axis domain across task, sub-task, and milestone dates so nothing
 * renders off-axis. Returns null when no parseable dates exist anywhere in the rows.
 */
export function computeGanttBounds(rows: ScheduleRowInput[]): GanttBounds | null {
  const values: number[] = [];
  const push = (dateKey: string | null | undefined) => {
    const ms = parseDateKeyToMs(dateKey);
    if (ms !== null) values.push(ms);
  };
  for (const row of rows) {
    push(row.startDateKey);
    push(row.finishDateKey);
    for (const subTask of row.subTasks ?? []) {
      push(subTask.startDateKey);
      push(subTask.finishDateKey);
      for (const milestone of subTask.milestones ?? []) {
        push(milestone.dueDateKey);
      }
    }
  }
  if (values.length === 0) return null;
  const minMs = Math.min(...values);
  const maxMs = Math.max(...values);
  // Pad the axis so the today line and edge bars are not flush against the borders.
  const padMs = Math.max(MS_PER_DAY, (maxMs - minMs) * 0.04);
  const paddedMin = minMs - padMs;
  const paddedMax = maxMs + padMs;
  return { minMs: paddedMin, maxMs: paddedMax, spanMs: Math.max(1, paddedMax - paddedMin) };
}

function useGanttBounds(rows: ScheduleRowInput[]): GanttBounds | null {
  return useMemo(() => computeGanttBounds(rows), [rows]);
}

/** Diamond markers for a sub-task's dated milestones, positioned on the shared timeline. */
function GanttMilestoneMarkers({
  milestones,
  toPct,
}: {
  milestones: FieldPilotSubTaskMilestone[];
  toPct: (ms: number) => number;
}) {
  return (
    <>
      {milestones.map((milestone) => {
        const ms = parseDateKeyToMs(milestone.dueDateKey);
        if (ms === null) return null;
        const color = milestone.colorHex ?? FIELD_PILOT_STATUS_FALLBACK_COLOR;
        return (
          <Tooltip key={String(milestone.statusId)}>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="absolute top-1/2 z-10 size-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[1px] border-2 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                style={{
                  left: `${toPct(ms)}%`,
                  backgroundColor: milestone.reached ? color : "var(--background)",
                  borderColor: color,
                }}
                aria-label={`${milestone.name ?? "Status"} ${milestone.reached ? "reached" : "upcoming"}`}
              />
            </TooltipTrigger>
            <TooltipContent>
              {(milestone.name ?? "Status")} · {formatScheduleDate(milestone.dueDateKey)} · {milestone.reached ? "Reached" : `${formatSchedulePercent(milestone.fraction)} of work reached this stage`}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
}

/** A timeline bar (left/width as %) with an actual-progress fill, used by tasks and sub-tasks. */
function GanttBar({
  row,
  startDateKey,
  finishDateKey,
  toPct,
  heightClass,
  title,
  milestones,
}: {
  row: { actualFraction: number; hasSchedule: boolean; isBehind: boolean };
  startDateKey: string | null;
  finishDateKey: string | null;
  toPct: (ms: number) => number;
  heightClass: string;
  title?: string;
  milestones?: FieldPilotSubTaskMilestone[];
}) {
  const start = parseDateKeyToMs(startDateKey);
  const finish = parseDateKeyToMs(finishDateKey);
  const hasBar = start !== null && finish !== null && finish >= start;
  if (!hasBar) {
    return (
      <span className="absolute top-1/2 left-1 -translate-y-1/2 text-[11px] text-muted-foreground">
        No dates set
      </span>
    );
  }
  const leftPct = toPct(start);
  const widthPct = Math.max(1, toPct(finish) - toPct(start));
  const actualPct = Math.round(clampFraction(row.actualFraction) * 100);
  const stageSegments = (milestones ?? [])
    .map((milestone) => ({ milestone, ms: parseDateKeyToMs(milestone.dueDateKey) }))
    .filter((entry): entry is { milestone: FieldPilotSubTaskMilestone; ms: number } => entry.ms !== null)
    .sort((a, b) => a.ms - b.ms);
  let previousMs = start;
  return (
    <div
      className={cn(
        "absolute top-1/2 -translate-y-1/2 overflow-hidden rounded border border-border bg-muted",
        heightClass
      )}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      title={title}
    >
      {stageSegments.length > 0
        ? stageSegments.map(({ milestone, ms }) => {
            const segmentStart = Math.max(start, previousMs);
            const segmentFinish = Math.min(finish, Math.max(segmentStart, ms));
            previousMs = ms;
            const left = ((segmentStart - start) / Math.max(1, finish - start)) * 100;
            const width =
              ((segmentFinish - segmentStart) / Math.max(1, finish - start)) *
              clampFraction(milestone.fraction) *
              100;
            if (width <= 0) return null;
            return (
              <div
                key={String(milestone.statusId)}
                className="absolute inset-y-0"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  backgroundColor:
                    milestone.colorHex ?? FIELD_PILOT_STATUS_FALLBACK_COLOR,
                }}
              />
            );
          })
        : (
            <div
              className="h-full"
              style={{ width: `${actualPct}%`, backgroundColor: progressColor(row) }}
            />
          )}
    </div>
  );
}

/** Shared date-axis Gantt chart with a "today" line, nested sub-tasks, and milestone markers. */
export function ScheduleGanttView({ rows }: { rows: ScheduleRowInput[] }) {
  const sorted = useMemo(
    () =>
      rows
        .slice()
        .sort(compareScheduleRows)
        .map((row) => ({
          ...row,
          subTasks: [...(row.subTasks ?? [])].sort(compareSubTasksByStartDate),
        })),
    [rows]
  );
  const bounds = useGanttBounds(sorted);

  if (sorted.length === 0) {
    return <EmptyState />;
  }
  if (!bounds) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        Set start and finish dates on at least one task to see the Gantt chart.
      </div>
    );
  }

  const toPct = (ms: number) => ((ms - bounds.minMs) / bounds.spanMs) * 100;
  const nowMs = Date.now();
  const todayPct = toPct(nowMs);
  const showToday = nowMs >= bounds.minMs && nowMs <= bounds.maxMs;

  return (
    <div className="space-y-1">
      <div className="flex justify-between px-2 text-[11px] text-muted-foreground">
        <span>{formatScheduleDate(new Date(bounds.minMs).toISOString().slice(0, 10))}</span>
        <span className="hidden items-center gap-3 sm:flex">
          <span>Filled segment = work at that stage</span>
          <span>◆ reached</span>
          <span>◇ upcoming</span>
          <span className="text-primary">│ today</span>
        </span>
        <span>{formatScheduleDate(new Date(bounds.maxMs).toISOString().slice(0, 10))}</span>
      </div>
      <div className="relative rounded-lg border">
        {showToday ? (
          // Today line maps timeline-relative % into the right 60% track (left gutter is 40%).
          <div
            className="pointer-events-none absolute inset-y-0 z-10 w-px bg-primary"
            style={{ left: `calc(40% + ${(todayPct * 0.6).toFixed(3)}%)` }}
            title="Today"
          />
        ) : null}
        {sorted.map((row) => {
          const dot = scheduleStatusDot(row);
          const subTasks = row.subTasks ?? [];
          return (
            <Fragment key={row.tagId}>
              <div className="grid grid-cols-[40%_60%] items-center gap-0 border-b px-0 last:border-b-0">
                <div className="flex items-center gap-2 px-3 py-2">
                  <span
                    className={cn(
                      "shrink-0",
                      statusTokenClasses(dot.status, {
                        visual: "dot",
                        size: "lg",
                        tone: dot.tone,
                      })
                    )}
                    role="img"
                    title={dot.label}
                    aria-label={dot.label}
                  />
                  <ScheduleRowLabel row={row} />
                </div>
                <div className="relative h-8">
                  <GanttBar
                    row={row}
                    startDateKey={row.startDateKey}
                    finishDateKey={row.finishDateKey}
                    toPct={toPct}
                    heightClass="h-4"
                    title={`${formatScheduleDate(row.startDateKey)} – ${formatScheduleDate(row.finishDateKey)} · ${formatVarianceDays(row.finishVarianceDays)}`}
                  />
                </div>
              </div>
              {subTasks.map((subTask) => {
                const isSolo = isSoloItemSchedule(subTask);
                return (
                <div
                  key={String(subTask.subTaskId)}
                  className={cn(
                    "grid grid-cols-[40%_60%] items-center gap-0 border-b px-0 last:border-b-0",
                    !isSolo && "bg-muted/20"
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center gap-1.5 py-1.5 pe-3",
                      isSolo ? "ps-3" : "ps-8"
                    )}
                  >
                    {!isSolo ? (
                      <CornerDownRight
                        className="size-3 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="min-w-0">
                      <div
                        className={cn("truncate font-medium", isSolo ? "text-sm" : "text-xs")}
                        title={subTask.name}
                      >
                        {subTask.name}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {subTask.currentStatus?.name ?? "—"}
                      </div>
                    </div>
                  </div>
                  <div className="relative h-7">
                    <GanttBar
                      row={subTask}
                      startDateKey={subTask.startDateKey}
                      finishDateKey={subTask.finishDateKey}
                      toPct={toPct}
                      heightClass={isSolo ? "h-4" : "h-3"}
                      title={`${formatScheduleDate(subTask.startDateKey)} – ${formatScheduleDate(subTask.finishDateKey)} · ${formatVarianceDays(subTask.finishVarianceDays)}`}
                      milestones={subTask.milestones ?? []}
                    />
                    <GanttMilestoneMarkers
                      milestones={subTask.milestones ?? []}
                      toPct={toPct}
                    />
                  </div>
                </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
      No tags selected yet. Select tags or add a custom line item to plan the schedule.
    </div>
  );
}
